import { WS_URL } from '@/config/env'

// Singleton WebSocket Manager fuera del contexto de React
export class GlobalWebSocketManager {
  private static instance: GlobalWebSocketManager | null = null
  private ws: WebSocket | null = null
  private isConnecting = false
  private subscribers = new Set<(conversationId?: string, message?: unknown) => void>()
  private reconnectTimeout: NodeJS.Timeout | null = null
  private isManualDisconnect = false
  private connectionState: 'connecting' | 'connected' | 'disconnected' | 'error' = 'disconnected'
  private stateListeners = new Set<(state: 'connecting' | 'connected' | 'disconnected' | 'error') => void>()
  private isInitialized = false

  static getInstance(): GlobalWebSocketManager {
    if (!GlobalWebSocketManager.instance) {
      GlobalWebSocketManager.instance = new GlobalWebSocketManager()
      // Inicializar automáticamente al crear la instancia
      GlobalWebSocketManager.instance.initialize()
    }
    return GlobalWebSocketManager.instance
  }

  private initialize() {
    if (this.isInitialized) return
    this.isInitialized = true

    // Conectar WebSocket con delay para evitar problemas con React Strict Mode
    setTimeout(() => {
      this.connect()
    }, 1000) // Delay de 1 segundo para que React termine de montar
  }

  private notifyStateChange() {
    this.stateListeners.forEach(listener => listener(this.connectionState))
  }

  subscribe(callback: (conversationId?: string, message?: unknown, eventType?: string) => void): () => void {
    this.subscribers.add(callback)
    return () => {
      this.subscribers.delete(callback)
    }
  }

  subscribeToState(callback: (state: 'connecting' | 'connected' | 'disconnected' | 'error') => void): () => void {
    this.stateListeners.add(callback)
    return () => {
      this.stateListeners.delete(callback)
    }
  }

  /** Usa VITE_WS_URL si está definida (p. ej. ws://host:8082/ws), si no la página actual. */
  private static buildFrontendSocketUrl(): string {
    const configured = (WS_URL || '').trim()
    if (configured) {
      const base = configured.replace(/\/$/, '')
      if (/\/frontend\/?$/i.test(base)) {
        const normalized = base.endsWith('/') ? base.slice(0, -1) : base
        return `${normalized}/?source=frontend-react`
      }
      if (/\/ws$/i.test(base)) {
        return `${base}/frontend/?source=frontend-react`
      }
      return `${base}/ws/frontend/?source=frontend-react`
    }
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    return `${protocol}//${host}/ws/frontend/?source=frontend-react`
  }

  connect() {
    // Si ya está conectado o conectando, no hacer nada
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return
    }

    // Limpiar conexión anterior si existe
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    // Limpiar timeout de reconexión anterior
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    this.isConnecting = true
    this.isManualDisconnect = false
    this.connectionState = 'connecting'
    this.notifyStateChange()

    try {
      const wsPath = GlobalWebSocketManager.buildFrontendSocketUrl()

      this.ws = new WebSocket(wsPath)

      this.ws.onopen = () => {
        this.isConnecting = false
        this.connectionState = 'connected'
        this.notifyStateChange()
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)


          switch (data.type) {
            case 'new_message': {
              // El backend envía: { type: 'new_message', data: { conversation_id, message, sender } }
              const newMessageData = data.data || {}


              this.subscribers.forEach((callback: (conversationId?: string, message?: unknown, eventType?: string) => void) => {
                callback(newMessageData.conversation_id, newMessageData, 'new_message')
              })
              break
            }

            case 'conversation_update': {
              // El backend envía: { type: 'conversation_update', data: { conversation_id, status } }
              const updateData = data.data || {}


              this.subscribers.forEach((callback: (conversationId?: string, message?: unknown, eventType?: string) => void) => {
                callback(updateData.conversation_id, updateData, 'conversation_update')
              })
              break
            }

            case 'connection_established':
              break

            case 'pong':
              break

            default:
          }
        } catch {
          // Ignorar errores de parsing
        }
      }

      this.ws.onclose = () => {

        this.isConnecting = false
        this.connectionState = 'disconnected'
        this.notifyStateChange()

        // Reconectar automáticamente si no fue desconexión manual
        if (!this.isManualDisconnect) {
          this.reconnectTimeout = setTimeout(() => {
            if (!this.isManualDisconnect) {
              this.connect()
            }
          }, 3000)
        }
      }

      this.ws.onerror = () => {
        this.isConnecting = false
        this.connectionState = 'error'
        this.notifyStateChange()
      }

    } catch {
      this.isConnecting = false
      this.connectionState = 'error'
      this.notifyStateChange()

      // Reintentar en caso de error
      this.reconnectTimeout = setTimeout(() => {
        if (!this.isManualDisconnect) {
          this.connect()
        }
      }, 3000)
    }
  }

  disconnect() {
    this.isManualDisconnect = true

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = null
    }

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    this.connectionState = 'disconnected'
    this.notifyStateChange()
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  getConnectionState(): 'connecting' | 'connected' | 'disconnected' | 'error' {
    return this.connectionState
  }
}
